import type { DataTypeDto, WaitingEntitiesDto } from '@bk/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { linkLabel } from '../../lib/links';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, ErrorText, Select } from '../../ui';

type Voce = WaitingEntitiesDto['voci'][number];

const entityName = (e: DataTypeDto['entita'][number]) => e.nome || String(e.attributi.nome ?? e.rif);

/**
 * Le voci lette che aspettano il loro genitore (ADR 0238 § 3): il movimento di un conto che non c'è ancora. Non sono
 * registrate; quelle che nominano il genitore entrano da sole quando arriva, le altre le fa entrare la persona scegliendo
 * il genitore, oppure le scarta.
 */
export function WaitingEntities({ type, types }: { type: DataTypeDto; types: DataTypeDto[] }) {
  const { isManager } = useTenant();
  const q = useTenantQuery<WaitingEntitiesDto>(['model', 'waiting', type.chiave], `/model/waiting?tipo=${encodeURIComponent(type.chiave)}`, { enabled: isManager });
  const list = q.data?.voci ?? [];
  if (!list.length) return null;
  return (
    <Card>
      <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
        In attesa del genitore <Badge tone="amber">{list.length}</Badge>
      </h2>
      <p className="mt-1 text-xs text-slate-500">
        Trovate dalle letture senza la cosa dentro cui vivono: non sono ancora registrate. Quelle che la nominano entrano da sole quando arriva; per le altre scegli tu, oppure scartale.
      </p>
      <ul className="mt-3 divide-y divide-slate-100">
        {list.map((v) => (
          <WaitingRow key={v.rif} v={v} type={type} types={types} />
        ))}
      </ul>
    </Card>
  );
}

function WaitingRow({ v, type, types }: { v: Voce; type: DataTypeDto; types: DataTypeDto[] }) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  // Il genitore da scegliere: il primo dei genitori possibili, o quello del gruppo che la persona cambia.
  const [which, setWhich] = useState(0);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const need = v.genitori[which] ?? v.genitori[0];
  const target = types.find((x) => x.chiave === need?.verso);
  const names = [...new Set((target?.entita ?? []).map(entityName))].sort((a, b) => a.localeCompare(b, 'it', { numeric: true }));
  const link = (type.legami_tipo ?? []).find((l) => l.significato === need?.significato && l.tipo === need?.verso);
  const read = v.genitori.find((g) => g.nome)?.nome ?? null;
  const done = async (path: string, body: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api(t(path), { body });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'model', 'waiting'] }),
        queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'data-model'] }),
      ]);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  const base = `/model/waiting/${encodeURIComponent(type.chiave)}/${encodeURIComponent(v.rif)}`;
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
        <span className="font-medium">{v.nome ?? v.rif}</span>
        <span className="text-xs text-slate-500">
          {read ? `nomina «${read}», che non è ancora tra le voci` : 'non dice dentro che cosa sta'} · da {v.fonte} · {dateTime(v.il)}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {v.genitori.length > 1 && (
          <Select className="w-auto rounded-lg border border-black/15 bg-white px-2 py-1.5 text-sm" value={which} onChange={(e) => { setWhich(Number(e.target.value)); setChoice(''); }} aria-label="Dentro che tipo sta">
            {v.genitori.map((g, i) => (
              <option key={`${g.significato}/${g.verso}`} value={i}>
                {types.find((x) => x.chiave === g.verso)?.etichetta ?? g.verso}
              </option>
            ))}
          </Select>
        )}
        <Select className="w-auto min-w-[14rem] rounded-lg border border-black/15 bg-white px-2 py-1.5 text-sm" value={choice} onChange={(e) => setChoice(e.target.value)} aria-label={link ? linkLabel(link, types) : 'Genitore'}>
          <option value="">{names.length ? `— ${(link ? linkLabel(link, types) : target?.etichetta ?? '').toLowerCase()} —` : `Nessuna voce in ${(target?.etichetta ?? need?.verso ?? '').toLowerCase()}`}</option>
          {names.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Select>
        <Button disabled={busy || !choice || !need} onClick={() => void done(base, { voci_legate: { [need!.significato]: choice } })}>
          Fai entrare
        </Button>
        <Button variant="ghost" disabled={busy} onClick={() => void done(`${base}/discard`, {})}>
          Scarta
        </Button>
      </div>
      <ErrorText>{error ?? ''}</ErrorText>
    </li>
  );
}
